import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';

import { useCustomer } from '../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderSetupForm = () => {
  const { customer } = useCustomer();
  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const hostName = useMemo(() => {
    const lastNameInitial = customer.lastName ? `${customer.lastName.charAt(0)}.` : undefined;

    return [customer.firstName, lastNameInitial].filter(Boolean).join(' ');
  }, [customer.firstName, customer.lastName]);

  const rules = useMemo(
    () => ({
      name: {
        required: formatMessage(messages.hostNameRequired),
        maxLength: {
          value: FIELD_MAX_LENGTH,
          message: formatMessage(messages.hostNameMaxLength, { max_length: FIELD_MAX_LENGTH }),
        },
      },
    }),
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  const form = useForm<GroupOrderSetupFormData>({
    defaultValues: { hostName },
    mode: 'onChange',
  });

  // ─────────────────────────────────────────────────────────────────────

  return {
    form,
    rules,
    maxLengths: { name: FIELD_MAX_LENGTH },
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  hostNameRequired: {
    defaultMessage: 'Name is required',
    description: 'Group Order Setup | Host name required error',
  },
  hostNameMaxLength: {
    defaultMessage: 'Name must be {max_length} characters or fewer',
    description: 'Group Order Setup | Host name max length error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FIELD_MAX_LENGTH = 50;

// ─── Types ───────────────────────────────────────────────────────────────────

export type GroupOrderSetupFormData = {
  hostName: string;
};
